import { useState } from 'react';
import { RESENA_MAXIMO_CARACTERES, RESENA_MINIMO_CARACTERES } from '@medchoice/shared';
import { ApiError } from '../../api/client';
import { paciente } from '../../api/paciente';
import { usePedido } from '../../api/usePedido';
import { irA } from '../../router';
import type { ScreenProps } from '../../routes/types';
import { AreaTexto, AvisoError, Boton, Cargando, FalloDeCarga, SelectorEstrellas, Tarjeta, useAvisos } from '../../ui';

export function Resena({ params }: ScreenProps) {
  const id = Number(params['id']);
  const { mostrarAviso } = useAvisos();
  const turno = usePedido(() => paciente.turno(id), [id]);
  const { datos: aspectos } = usePedido(() => paciente.aspectos(), []);
  const [calificacion, setCalificacion] = useState(0);
  const [comentario, setComentario] = useState('');
  const [elegidos, setElegidos] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  if (turno.estado === 'cargando') return <Cargando />;
  const t = turno.datos;
  if (!t) return <FalloDeCarga mensaje={turno.error ?? 'No encontramos el turno'} alReintentar={turno.recargar} />;

  const largo = comentario.trim().length;
  const valida = calificacion > 0 && largo >= RESENA_MINIMO_CARACTERES;
  const alternar = (aspecto: string) =>
    setElegidos((actuales) => (actuales.includes(aspecto) ? actuales.filter((a) => a !== aspecto) : [...actuales, aspecto]));

  const publicar = async () => {
    setEnviando(true);
    try {
      await paciente.resenar(id, { calificacion, comentario: comentario.trim(), aspectos: elegidos });
      mostrarAviso({ tono: 'exito', titulo: 'Gracias por tu reseña', cuerpo: 'Ya aparece en el perfil del profesional.' });
      irA('/mis-turnos', { reemplazar: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No pudimos publicar la reseña. Probá de nuevo.');
      setEnviando(false);
    }
  };

  return (
    <div className="resena">
      <Tarjeta>
        <h1 className="reserva__titulo">¿Cómo te fue con {t.profesional.nombre} {t.profesional.apellido}?</h1>
        <p className="reserva__bajada">{t.servicio.nombre}</p>
        <AvisoError>{error}</AvisoError>
        <SelectorEstrellas valor={calificacion} alCambiar={setCalificacion} etiqueta="Tu puntaje" />
        {aspectos && (
          <fieldset className="reserva__grupo">
            <legend>¿Qué destacarías?</legend>
            <div className="chips">
              {aspectos.map((a) => (
                <button key={a} type="button" aria-pressed={elegidos.includes(a)} onClick={() => alternar(a)}
                  className={elegidos.includes(a) ? 'chip chip--coral chip--boton' : 'chip chip--boton'}>{a}</button>
              ))}
            </div>
          </fieldset>
        )}
        <AreaTexto etiqueta="Contá un poco más" maxLength={RESENA_MAXIMO_CARACTERES} value={comentario}
          onChange={(e) => setComentario(e.target.value)}
          ayuda={largo < RESENA_MINIMO_CARACTERES
            ? `Te faltan ${RESENA_MINIMO_CARACTERES - largo} caracteres. Tu reseña es pública.`
            : 'Tu reseña es pública.'} />
        <div className="reserva__acciones">
          <Boton variante="secundario" onClick={() => irA('/mis-turnos')}>Ahora no</Boton>
          <Boton disabled={!valida} cargando={enviando} onClick={() => void publicar()}>Publicar reseña</Boton>
        </div>
      </Tarjeta>
    </div>
  );
}
